import { Component, EventEmitter, Input, Output, SimpleChanges, OnChanges, AfterViewInit, OnInit, OnDestroy, Renderer2, Inject, ViewChild, ViewEncapsulation } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { Subscription } from 'rxjs';
import { AutocompleteService, AutocompleteOption, AutocompleteRequest } from '../../Services/autocomplete.service';
import { TgSettingsService } from '../../../../services/TG-Setting_Services/tg-settings.service';

@Component({
  selector: 'app-primeng-auto-complete',
  standalone: true,
  imports: [CommonModule, FormsModule, AutoCompleteModule],
  templateUrl: './primeng-auto-complete.component.html',
  styleUrls: ['./primeng-auto-complete.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PrimengAutoCompleteComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input() question!: any;
  @Output() selectedChange = new EventEmitter<AutocompleteOption[]>();

  @ViewChild('tgAutoComplete') tgAutoComplete!: any;

  isNavigatingList = false;
  partnerId = Number((document.getElementById('partnerId') as HTMLInputElement)?.value);
  siteId = Number((document.getElementById('siteId') as HTMLInputElement)?.value);
  lastKeyboardAction: 'type' | 'navigate' = 'type';
  options: AutocompleteOption[] = [];
  selected: AutocompleteOption[] = [];
  dummyArray: any[] = [];
  searchText = '';
  loading = false;
  highlightedIndex: number = -1;
  srResultsMessage = '';
  private themeSub!: Subscription;

  pageSize = 25;
  currentPage = 0;
  hasMoreData = true;

  constructor(
    private autoSvc: AutocompleteService,
    private tgSettings: TgSettingsService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) { }

  ngOnInit(): void {
    this.applyThemeVariables(this.tgSettings.getBrandingConfig());
  }

  ngAfterViewInit(): void {
    // Apply structural attributes that don't depend on the overlay
    this.setupStaticAccessibility();
  }

  // ==========================================
  // STATIC ACCESSIBILITY (Runs Once)
  // ==========================================
  private setupStaticAccessibility() {
    const tryApply = () => {
      const root = this.tgAutoComplete?.el?.nativeElement;
      if (!root) {
        setTimeout(tryApply, 50);
        return;
      }
      
      const inputEl = root.querySelector('input.p-autocomplete-dd-input, input.p-autocomplete-input, .p-autocomplete-input-token input');
      if (inputEl) {
        const qid = this.question?.QId;
        this.renderer.setAttribute(inputEl, 'id', 'text_' + qid);
        this.renderer.setAttribute(inputEl, 'name', 'text_' + this.question?.qid);
        this.renderer.setAttribute(inputEl, 'aria-labelledby', 'label_' + qid);
        this.renderer.setAttribute(inputEl, 'autocomplete', 'off');
      }
      
      const dropdownBtn = root.querySelector('.p-autocomplete-dropdown');
      if (dropdownBtn) {
        this.renderer.setAttribute(dropdownBtn, 'aria-label', 'Open options');
      }
      
      const listbox = root.querySelector('[role="listbox"]');
      if (listbox) {
        this.renderer.setAttribute(listbox, 'aria-labelledby', 'label_' + this.question?.QId);
      }
      
      const chips = root.querySelectorAll('.p-autocomplete-input-chip');
      chips.forEach((chip: any) => {
        this.renderer.removeAttribute(chip, 'role');
      });
    };
    tryApply();
  }

  // ==========================================
  // DYNAMIC ACCESSIBILITY (Triggered by onShow & Data Load)
  // ==========================================
  onPanelShow(): void {
    // Keep Tab focus on the input — PrimeNG sets tabindex="0" on li[role="option"] by default,
    // which causes Tab to enter the dropdown and Enter on those items to bubble to the form.
    setTimeout(() => {
      const items = this.document.querySelectorAll('li[role="option"]');
      items.forEach((item: any) => {
        this.renderer.setAttribute(item, 'tabindex', '-1');
      });
    }, 0);
  }

  public applyDynamicAccessibilityFixes() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const options = this.document.querySelectorAll('li[role="option"]');
        options.forEach((opt: any) => {
          if (opt.hasAttribute('aria-label')) {
            this.renderer.removeAttribute(opt, 'aria-label');
          }
        });

        const count = this.options.length;
        this.srResultsMessage = count === 0 ? 'No results found.' : `${count} option${count === 1 ? '' : 's'} available.`;
      });
    });
  }

  onKeyDown(event: KeyboardEvent) {
    const overlayOpen = this.tgAutoComplete?.overlayVisible;
    if (event.key === 'ArrowDown') {
      this.lastKeyboardAction = 'navigate';
      if (!overlayOpen) {
        this.tgAutoComplete?.show();
        this.highlightedIndex = -1;
        return;
      }
      event.preventDefault();
      this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.options.length - 1);
      this.scrollToHighlighted();
      return;
    }
    if (event.key === 'ArrowUp') {
      this.lastKeyboardAction = 'navigate';
      if (!overlayOpen) return;
      event.preventDefault();
      this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
      this.scrollToHighlighted();
      return;
    }
    if ((event.code === 'Space' || event.key === ' ' || event.key === 'Enter') && overlayOpen) {
      // Always prevent default when the overlay is open — prevents form submission on Enter
      // and page scroll on Space regardless of whether an option is highlighted.
      event.preventDefault();
      event.stopPropagation();
      const inputEl = this.document.querySelector('.tg-structural-autocomplete input') as HTMLInputElement | null;
      const activeItem = this.getActivePanelItem(inputEl);
      if (activeItem) {
        const li = activeItem.closest('li');
        const items = this.document.querySelectorAll('.p-autocomplete-list .p-autocomplete-option');
        const index = Array.from(items).indexOf(li as Element);
        if (index >= 0 && this.options[index]) {
          this.toggleItem(this.options[index]);
        }
      }
      return;
    }
    if (event.key === 'Tab' && overlayOpen) {
      // Close the overlay on Tab so focus moves naturally to the next element.
      // Do NOT prevent default — Tab must still advance focus per WCAG 2.1.1.
      this.tgAutoComplete?.hide();
      this.highlightedIndex = -1;
      this.clearActiveDescendant();
      return;
    }
    if (event.key === 'Escape') {
      this.tgAutoComplete?.hide();
      this.highlightedIndex = -1;
      this.clearActiveDescendant();
      return;
    }
    if (!['Tab', 'Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) {
      this.lastKeyboardAction = 'type';
      this.highlightedIndex = -1;
    }
  }

  private scrollToHighlighted() {
    setTimeout(() => {
      const panel = this.document.querySelector('.tg-solid-panel .p-autocomplete-items');
      if (!panel) return;
      const items = panel.querySelectorAll('li');
      const target = items[this.highlightedIndex] as HTMLElement;
      if (target) {
        target.scrollIntoView({ block: 'nearest' });
        const root = this.tgAutoComplete?.el?.nativeElement;
        const inputEl = root?.querySelector('input.p-autocomplete-dd-input, input.p-autocomplete-input');
        if (inputEl) {
          if (target.id) {
            this.renderer.setAttribute(inputEl, 'aria-activedescendant', target.id);
          } else {
            const generatedId = 'tg-opt-' + this.highlightedIndex;
            this.renderer.setAttribute(target, 'id', generatedId);
            this.renderer.setAttribute(inputEl, 'aria-activedescendant', generatedId);
          }
        }
      }
    }, 0);
  }

  private clearActiveDescendant() {
    const root = this.tgAutoComplete?.el?.nativeElement;
    const inputEl = root?.querySelector('input.p-autocomplete-dd-input, input.p-autocomplete-input');
    if (inputEl) {
      this.renderer.removeAttribute(inputEl, 'aria-activedescendant');
    }
  }

  private getActivePanelItem(inputEl: HTMLInputElement | null): HTMLElement | null {
    if (inputEl) {
      const activeId = inputEl.getAttribute('aria-activedescendant');
      if (activeId) {
        const el = this.document.getElementById(activeId);
        if (el) return el as HTMLElement;
      }
    }
    const byFocus = this.document.querySelector('.tg-solid-panel li.p-focus, .p-autocomplete-panel li.p-focus') as HTMLElement;
    if (byFocus) return byFocus;
    const byHighlight = this.document.querySelector('.tg-solid-panel li.p-highlight, .p-autocomplete-panel li.p-highlight') as HTMLElement;
    if (byHighlight) return byHighlight;
    return null;
  }

  ngOnDestroy(): void {
    if (this.themeSub) this.themeSub.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['question'] && this.question) {
      if (!this.question.Options || this.question.Options.length === 0) {
        this.question.AjaxLoad = true;
      }

      if (!this.question.AjaxLoad) {
        this.loadStaticOptions('');
      } else {
        this.searchText = '';        
        this.currentPage = 0; 
        this.hasMoreData = true; 
        this.fetchAjaxData();
      }
      this.syncRestoredSelectedOptions();
    }
  }

  onSelectionChange() {
    this.selectedChange.emit([...this.selected]);
  }

  isSelected(opt: AutocompleteOption): boolean {
    return this.selected.some(s => s.data === opt.data);
  }

  onOptionClick(event: Event, opt: AutocompleteOption) {
    event.stopPropagation();
    event.preventDefault();
    this.toggleItem(opt);
    this.clearSearchText();
  }

  private clearSearchText() {
    if (this.tgAutoComplete && this.tgAutoComplete.el) {
      const inputEl = this.tgAutoComplete.el.nativeElement.querySelector('input');
      if (inputEl) {
        inputEl.value = ''; 
      }
    }
    setTimeout(() => {
      this.dummyArray = [];
      this.searchText = '';
    }, 50);
  }

  onItemSelect(event: any) {
    if (event && event.label && event.data) {
      this.toggleItem(event as AutocompleteOption);
    }
    this.clearSearchText();
  }

  onItemUnselect(event: any) {
    if (event && event.label && event.data) {
      this.toggleItem(event as AutocompleteOption);
    }
    setTimeout(() => { this.dummyArray = []; });
  }

  toggleItem(opt: AutocompleteOption) {
    if (!opt || !opt.data) return;
    const existsIndex = this.selected.findIndex(s => s.data === opt.data);
    if (existsIndex > -1) {
      this.selected.splice(existsIndex, 1);
    } else {
      this.selected.push(opt);
    }
    this.onSelectionChange();
  }

  removeTag(event: Event, item: AutocompleteOption) {
    event.stopPropagation();
    this.selected = this.selected.filter(s => s.data !== item.data);
    this.onSelectionChange();
  }

  onSearch(event: any) {
    this.searchText = event.query || '';
    if (this.question?.AjaxLoad) {
      this.currentPage = 0; 
      this.hasMoreData = true;
      this.fetchAjaxData();
    } else {
      this.loadStaticOptions(this.searchText);
    }
    // Removed old timeout panel landmark logic since applyDynamicAccessibilityFixes handles it safely
  }

  onLazyLoad(event: any) {
    if (this.question?.AjaxLoad) {
      if (!this.loading && this.hasMoreData && this.options.length > 0) {
        this.currentPage++;
        this.fetchAjaxData(); 
      }
    }
  }

  private fetchAjaxData() {
    if (this.loading || !this.hasMoreData) return;
    
    this.loading = true;
    
    const req: AutocompleteRequest = {
      PartnerId: this.partnerId.toString(), SiteId: this.siteId.toString(),
      QuestionId: this.question.QId.toString(), SearchString: this.searchText,
      SelectedQuestionCodes: this.selected.map(s => s.data).join('#@#'),
      PageIndex: this.currentPage, 
      PageSize: this.pageSize
    } as any;

    this.autoSvc.getSuggestions(req, this.getRftToken(), '/TgNewUI/Search/Ajax/PowerSearchAutoComplete').subscribe({
      next: (res: AutocompleteOption[]) => {
        const rawResponse = res || [];
        if(this.pageSize == 1){
          this.options = [];
        }
        const incoming = rawResponse.filter(x => !this.selected.some(s => s.data === x.data));

        if (this.currentPage === 0) { 
          this.options = [...incoming]; 
        } else { 
          const uniqueIncoming = incoming.filter(item => !this.options.some(o => o.data === item.data));
          this.options = [...this.options, ...uniqueIncoming]; 
        }
        
        this.hasMoreData = rawResponse.length >= this.pageSize;
        this.loading = false;
        
        // 🔥 Trigger dynamic accessibility immediately after new data populates
        this.applyDynamicAccessibilityFixes();
      },
      error: () => { 
        this.loading = false; 
        this.hasMoreData = false;
      }
    });
  }

  private loadStaticOptions(searchStr: string) {
    const search = searchStr.toLowerCase();
    const all: AutocompleteOption[] = (this.question.Options || []).map((o: any) => ({
      data: String(o.OptionValue), label: String(o.OptionName), value: String(o.OptionName)
    }));
    this.options = all.filter(o => !search || o.label.toLowerCase().includes(search));
    
    // 🔥 Trigger dynamic accessibility immediately after local filtering
    this.applyDynamicAccessibilityFixes();
  }

  private syncRestoredSelectedOptions() {
    if (this.question.SelectedOptions && Array.isArray(this.question.SelectedOptions)) {
      this.selected = this.question.SelectedOptions.map((code: string) => {
        const existingOp = this.options.find(o => o.data === code);
        if (existingOp) return existingOp;
        const staticOp = this.question.Options?.find((o: any) => String(o.OptionValue) === code);
        if (staticOp) {
          return { data: String(staticOp.OptionValue), label: String(staticOp.OptionName), value: String(staticOp.OptionName) };
        }
        return { data: code, label: code, value: code };
      });
    }
  }

  private getRftToken(): string {
    return (document.querySelector("input[name='__RequestVerificationToken']") as HTMLInputElement)?.value || '';
  }

  private applyThemeVariables(styles: any) {
    const root = this.document.documentElement;
    
    // Ensure styles is defined; if null, use defaults
    if (!styles) styles = {};
    
    root.style.setProperty('--tg-bg', styles.bg || '#ffffff');
    root.style.setProperty('--tg-text', styles.text || 'darkgreen');
    root.style.setProperty('--tg-focus', styles.focus || '#990099');
    root.style.setProperty('--tg-font', styles.font || 'Arial, sans-serif');
    root.style.setProperty('--tg-hover-bg', styles.hoverBg || '#adc6bc');
    root.style.setProperty('--tg-active-border', styles.activeBorder || 'none');
    root.style.setProperty('--tg-control-height', styles.controlHeight || '30px');
    root.style.setProperty('--tg-active-text', styles.activeText || '#333333');
    root.style.setProperty('--tg-control-max-width', styles.controlMaxWidth || '300px');
  }
}